import React, { useState } from "react";
import { Progress, Table, Tag, Space } from "antd";
import { FiAlertTriangle, FiAlertCircle, FiCheckCircle, FiShield, FiTrendingUp, FiClock } from "react-icons/fi";
import { Helmet } from "react-helmet-async";

export default function ThreatPreventionPage() {
    const [activeTab, setActiveTab] = useState("overview");

    // Summary Statistics
    const threatStats = {
        activeThreatBlockedToday: 127,
        suspiciousActivityDetected: 34,
        preventionSuccessRate: 98.7,
        threatIntelligenceUpdates: 456,
        averageResponseTime: "2.3s",
        systemHealthScore: 94,
    };

    // Active Threats Data
    const activeThreats = [
        {
            id: "THREAT-001",
            threatType: "Malware Detection",
            severity: "Critical",
            affectedSystems: ["SCADA-01", "HMI-01"],
            detectionTime: "2025-01-02 14:32:15",
            status: "Blocked",
            sourceIP: "192.168.1.155",
            description: "Suspicious process injection detected in control zone",
            actionTaken: "Process terminated & isolated",
            confidence: "99.5%",
        },
        {
            id: "THREAT-002",
            threatType: "Unauthorized Access Attempt",
            severity: "High",
            affectedSystems: ["MGMT-SERVER-01"],
            detectionTime: "2025-01-02 14:28:42",
            status: "Blocked",
            sourceIP: "10.0.5.89",
            description: "Multiple failed login attempts detected",
            actionTaken: "Account locked & alerts sent",
            confidence: "98.2%",
        },
        {
            id: "THREAT-003",
            threatType: "Network Anomaly",
            severity: "High",
            affectedSystems: ["INDUSTRIAL-SWITCH", "PLC-01"],
            detectionTime: "2025-01-02 14:15:33",
            status: "Contained",
            sourceIP: "192.168.1.200",
            description: "Abnormal data flow pattern detected in field devices zone",
            actionTaken: "Traffic rerouted & monitored",
            confidence: "94.8%",
        },
        {
            id: "THREAT-004",
            threatType: "Protocol Anomaly",
            severity: "Medium",
            affectedSystems: ["SENSOR-NETWORK"],
            detectionTime: "2025-01-02 14:02:18",
            status: "Monitored",
            sourceIP: "192.168.2.75",
            description: "Non-standard MODBUS communication detected",
            actionTaken: "Connection logged & analyzed",
            confidence: "87.3%",
        },
        {
            id: "THREAT-005",
            threatType: "Vulnerability Exploit Attempt",
            severity: "High",
            affectedSystems: ["LOG-SERVER"],
            detectionTime: "2025-01-02 13:48:06",
            status: "Blocked",
            sourceIP: "203.0.113.45",
            description: "CVE-2024-1234 exploitation pattern detected",
            actionTaken: "Exploit blocked & patch scheduled",
            confidence: "96.7%",
        },
        {
            id: "THREAT-006",
            threatType: "Data Exfiltration",
            severity: "Critical",
            affectedSystems: ["HISTORIAN-01"],
            detectionTime: "2025-01-02 13:35:22",
            status: "Blocked",
            sourceIP: "192.168.1.120",
            description: "Unusual data volume transferred to external IP",
            actionTaken: "Transmission halted & source isolated",
            confidence: "99.1%",
        },
    ];

    // Threat Prevention History
    const preventionHistory = [
        {
            id: "PREV-001",
            date: "2025-01-02",
            threatsDetected: 127,
            threatsPrevented: 124,
            responseTime: "2.1s",
            successRate: "97.6%",
            topThreatType: "Malware",
            systemsAffected: 8,
        },
        {
            id: "PREV-002",
            date: "2025-01-01",
            threatsDetected: 156,
            threatsPrevented: 154,
            responseTime: "2.4s",
            successRate: "98.7%",
            topThreatType: "Network Anomaly",
            systemsAffected: 12,
        },
        {
            id: "PREV-003",
            date: "2024-12-31",
            threatsDetected: 98,
            threatsPrevented: 96,
            responseTime: "2.2s",
            successRate: "98.0%",
            topThreatType: "Unauthorized Access",
            systemsAffected: 5,
        },
        {
            id: "PREV-004",
            date: "2024-12-30",
            threatsDetected: 143,
            threatsPrevented: 140,
            responseTime: "2.5s",
            successRate: "97.9%",
            topThreatType: "Protocol Anomaly",
            systemsAffected: 10,
        },
    ];

    // Detection Methods
    const detectionMethods = [
        { name: "Signature-based Detection", count: 2145, percentage: 35.2, status: "Active" },
        { name: "Behavioral Analysis", count: 1876, percentage: 30.8, status: "Active" },
        { name: "Anomaly Detection", count: 1542, percentage: 25.3, status: "Active" },
        { name: "Threat Intelligence", count: 645, percentage: 10.6, status: "Active" },
        { name: "Zero-day Protection", count: 123, percentage: 2.0, status: "Active" },
    ];

    // Vulnerability Status
    const vulnerabilityStatus = [
        { category: "Critical", count: 3, action: "Immediate Patch", color: "#ef4444" },
        { category: "High", count: 12, action: "Patch This Week", color: "#f97316" },
        { category: "Medium", count: 34, action: "Patch Next Month", color: "#eab308" },
        { category: "Low", count: 78, action: "Patch Quarterly", color: "#84cc16" },
    ];

    // Helper functions
    const getSeverityColor = (severity) => {
        const colors = {
            Critical: "#ef4444",
            High: "#f97316",
            Medium: "#eab308",
            Low: "#84cc16",
        };
        return colors[severity] || "#6b7280";
    };

    const getSeverityBg = (severity) => {
        const backgrounds = {
            Critical: "#fee2e2",
            High: "#fed7aa",
            Medium: "#fef3c7",
            Low: "#dcfce7",
        };
        return backgrounds[severity] || "#f3f4f6";
    };

    const getStatusColor = (status) => {
        const colors = {
            Blocked: "#10b981",
            Contained: "#f59e0b",
            Monitored: "#3b82f6",
        };
        return colors[status] || "#6b7280";
    };

    // Table columns for Active Threats
    const activeThreatsColumns = [
        {
            title: "Threat ID",
            dataIndex: "id",
            key: "id",
            render: (text) => <span className="fw-bold text-gray-900">{text}</span>,
            width: 100,
        },
        {
            title: "Threat Type",
            dataIndex: "threatType",
            key: "threatType",
        },
        {
            title: "Severity",
            dataIndex: "severity",
            key: "severity",
            render: (severity) => (
                <Tag color={getSeverityColor(severity)} style={{ borderRadius: "4px" }}>
                    {severity}
                </Tag>
            ),
        },
        {
            title: "Detection Time",
            dataIndex: "detectionTime",
            key: "detectionTime",
            width: 160,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={getStatusColor(status)} style={{ borderRadius: "4px" }}>
                    {status}
                </Tag>
            ),
        },
        {
            title: "Confidence",
            dataIndex: "confidence",
            key: "confidence",
            render: (conf) => <span className="fw-bold">{conf}</span>,
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <button type="button" className="btn btn-sm btn-primary fw-bold">
                    Details
                </button>
            ),
            width: 100,
        },
    ];

    // Table columns for Prevention History
    const preventionHistoryColumns = [
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
        },
        {
            title: "Detected",
            dataIndex: "threatsDetected",
            key: "threatsDetected",
            render: (count) => <span className="fw-bold text-danger">{count}</span>,
        },
        {
            title: "Prevented",
            dataIndex: "threatsPrevented",
            key: "threatsPrevented",
            render: (count) => <span className="fw-bold text-success">{count}</span>,
        },
        {
            title: "Success Rate",
            dataIndex: "successRate",
            key: "successRate",
            render: (rate) => {
                const percentage = parseFloat(rate);
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Progress type="circle" percent={percentage} width={40} strokeColor={percentage > 98 ? "#10b981" : "#f59e0b"} />
                        <span className="fw-bold">{rate}</span>
                    </div>
                );
            },
        },
        {
            title: "Response Time",
            dataIndex: "responseTime",
            key: "responseTime",
        },
        {
            title: "Top Threat",
            dataIndex: "topThreatType",
            key: "topThreatType",
        },
        {
            title: "Systems Affected",
            dataIndex: "systemsAffected",
            key: "systemsAffected",
            render: (count) => <Tag color="blue">{count} systems</Tag>,
        },
    ];

    return (



        <>
            <Helmet>
                <title>Network | Sec1 Software Pvt. Ltd.</title>
            </Helmet>

            <div id="kt_app_toolbar" className="app-toolbar pt-12 rounded-0">
                <div
                    id="kt_app_toolbar_container"
                    className="app-container container-fluid d-flex align-items-stretch"
                >
                    <div className="app-toolbar-wrapper d-flex flex-stack flex-wrap gap-4 w-100">
                        <div className="page-title d-flex flex-column gap-1 me-3 mb-2">
                            <h1 className="page-heading d-flex flex-column justify-content-center text-dark fw-bolder fs-1 lh-0">
                                Threat Prevention
                            </h1>
                        </div>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div
                    id="kt_app_content_container"
                    className="app-container container-fluid"
                >
                    <div className="mb-3">


                        <div className="d-flex flex-column gap-4 mb-5">
                            {/* Page Header */}
                            {/* <div style={{ background: "linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(6, 182, 212, 0.04) 100%)", borderRadius: "12px", padding: "32px 28px", border: "1px solid rgba(59, 130, 246, 0.1)" }}>
                                <div className="d-flex align-items-center gap-3 mb-2">
                                    <div style={{ width: "56px", height: "56px", borderRadius: "10px", background: "linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(59, 130, 246, 0.3)" }}>
                                        <FiAlertTriangle size={32} color="white" />
                                    </div>
                                    <div>
                                        <h1 className="fw-bold text-gray-900 mb-1 fs-3">Threat Prevention & Detection</h1>
                                        <p className="text-gray-600 mb-0">Real-time OT infrastructure security monitoring and threat response</p>
                                    </div>
                                </div>
                            </div> */}

                            <div className="row g-2 mb-4">
                                <div className="col-lg-3 col-md-6 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #ef4444", borderRadius: "8px", boxShadow: "0 4px 20px rgba(239, 68, 68, 0.15)", background: "linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">Threats Blocked Today</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{threatStats.activeThreatBlockedToday}</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(239, 68, 68, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiAlertTriangle size={28} color="#ef4444" />
                                                </div>
                                            </div>
                                            <div className="mt-3">
                                                <small className="text-gray-600 fw-semibold">↑ 15% from yesterday</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-3 col-md-6 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #f59e0b", borderRadius: "8px", boxShadow: "0 4px 20px rgba(245, 158, 11, 0.15)", background: "linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">Suspicious Activity</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{threatStats.suspiciousActivityDetected}</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(245, 158, 11, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiAlertCircle size={28} color="#f59e0b" />
                                                </div>
                                            </div>
                                            <div className="mt-3">
                                                <small className="text-gray-600 fw-semibold">Under investigation</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-3 col-md-6 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #10b981", borderRadius: "8px", boxShadow: "0 4px 20px rgba(16, 185, 129, 0.15)", background: "linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">Prevention Success Rate</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{threatStats.preventionSuccessRate}%</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(16, 185, 129, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiCheckCircle size={28} color="#10b981" />
                                                </div>
                                            </div>
                                            <div className="mt-3">
                                                <small className="text-gray-600 fw-semibold">↑ 0.5% from last week</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-3 col-md-6 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #3b82f6", borderRadius: "8px", boxShadow: "0 4px 20px rgba(59, 130, 246, 0.15)", background: "linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">Threat Intel Updates</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{threatStats.threatIntelligenceUpdates}</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(59, 130, 246, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiTrendingUp size={28} color="#3b82f6" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-3 col-md-6 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #8b5cf6", borderRadius: "8px", boxShadow: "0 4px 20px rgba(139, 92, 246, 0.15)", background: "linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">Avg Response Time</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{threatStats.averageResponseTime}</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(139, 92, 246, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiClock size={28} color="#8b5cf6" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-3 col-md-6 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #06b6d4", borderRadius: "8px", boxShadow: "0 4px 20px rgba(6, 182, 212, 0.15)", background: "linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">System Health Score</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{threatStats.systemHealthScore}/100</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(6, 182, 212, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiShield size={28} color="#06b6d4" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Tab Navigation */}
                            <div className="d-flex gap-3 border-bottom mb-5 pb-2" style={{ background: "linear-gradient(to right, rgba(59, 130, 246, 0.02), rgba(0, 0, 0, 0))" }}>
                                <button
                                    type="button"
                                    className={`btn btn-outline-0 fw-bold pb-3 fs-6 position-relative ${activeTab === "overview" ? "text-primary" : "text-gray-600"}`}
                                    onClick={() => setActiveTab("overview")}
                                    style={{ borderRadius: 0, background: "transparent", border: "none", transition: "all 0.3s ease" }}
                                >
                                    Active Threats
                                    {activeTab === "overview" && <div style={{ position: "absolute", bottom: "-12px", left: "0", right: "0", height: "3px", background: "linear-gradient(to right, #3b82f6, #06b6d4)", borderRadius: "2px" }} />}
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-outline-0 fw-bold pb-3 fs-6 position-relative ${activeTab === "history" ? "text-primary" : "text-gray-600"}`}
                                    onClick={() => setActiveTab("history")}
                                    style={{ borderRadius: 0, background: "transparent", border: "none", transition: "all 0.3s ease" }}
                                >
                                    Prevention History
                                    {activeTab === "history" && <div style={{ position: "absolute", bottom: "-12px", left: "0", right: "0", height: "3px", background: "linear-gradient(to right, #3b82f6, #06b6d4)", borderRadius: "2px" }} />}
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-outline-0 fw-bold pb-3 fs-6 position-relative ${activeTab === "detection" ? "text-primary" : "text-gray-600"}`}
                                    onClick={() => setActiveTab("detection")}
                                    style={{ borderRadius: 0, background: "transparent", border: "none", transition: "all 0.3s ease" }}
                                >
                                    Detection Methods
                                    {activeTab === "detection" && <div style={{ position: "absolute", bottom: "-12px", left: "0", right: "0", height: "3px", background: "linear-gradient(to right, #3b82f6, #06b6d4)", borderRadius: "2px" }} />}
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-outline-0 fw-bold pb-3 fs-6 position-relative ${activeTab === "vulnerabilities" ? "text-primary" : "text-gray-600"}`}
                                    onClick={() => setActiveTab("vulnerabilities")}
                                    style={{ borderRadius: 0, background: "transparent", border: "none", transition: "all 0.3s ease" }}
                                >
                                    Vulnerabilities
                                    {activeTab === "vulnerabilities" && <div style={{ position: "absolute", bottom: "-12px", left: "0", right: "0", height: "3px", background: "linear-gradient(to right, #3b82f6, #06b6d4)", borderRadius: "2px" }} />}
                                </button>
                            </div>

                            {/* Tab Content - Active Threats */}
                            {activeTab === "overview" && (
                                <div className="card mt-5">
                                    <div className="card-body">
                                        <h5 className="fw-bold text-gray-900 mb-4">Active Threat Incidents</h5>
                                        <Table
                                            dataSource={activeThreats}
                                            columns={activeThreatsColumns}
                                            rowKey="id"
                                            pagination={{ pageSize: 10, className: "mb-0" }}
                                            scroll={{ x: 1200 }}
                                            className="antd-override"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Tab Content - Prevention History */}
                            {activeTab === "history" && (
                                <div className="card mt-5">
                                    <div className="card-body">
                                        <h5 className="fw-bold text-gray-900 mb-4">Daily Prevention Summary</h5>
                                        <Table
                                            dataSource={preventionHistory}
                                            columns={preventionHistoryColumns}
                                            rowKey="id"
                                            pagination={{ pageSize: 10, className: "mb-0" }}
                                            scroll={{ x: 1200 }}
                                            className="antd-override"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Tab Content - Detection Methods */}
                            {activeTab === "detection" && (
                                <div className="card mt-5">
                                    <div className="card-body">
                                        <h5 className="fw-bold text-gray-900 mb-4">Detection Method Breakdown</h5>
                                        <div className="row g-3">
                                            {detectionMethods.map((method) => (
                                                <div key={method.name} className="col-12 col-lg-6">
                                                    <div className="p-4 border rounded" style={{ borderColor: "#e5e7eb", background: "linear-gradient(135deg, rgba(59, 130, 246, 0.02) 0%, rgba(0, 0, 0, 0) 100%)", borderLeft: "4px solid #3b82f6", boxShadow: "0 2px 8px rgba(59, 130, 246, 0.08)" }}>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <div>
                                                                <h6 className="fw-bold text-gray-900 mb-1">{method.name}</h6>
                                                                <p className="text-gray-600 fs-6 mb-0">{method.count.toLocaleString()} detections</p>
                                                            </div>
                                                            <Tag color="blue" style={{ borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>
                                                                {method.percentage}%
                                                            </Tag>
                                                        </div>
                                                        <Progress percent={method.percentage} strokeColor="#3b82f6" />
                                                        <div className="mt-3 d-flex gap-2">
                                                            <span className="badge bg-success fw-semibold">{method.status}</span>
                                                            <span className="badge bg-info fw-semibold">Active</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Tab Content - Vulnerabilities */}
                            {activeTab === "vulnerabilities" && (
                                <div className="card mt-5">
                                    <div className="card-body">
                                        <h5 className="fw-bold text-gray-900 mb-4">Vulnerability Management Status</h5>
                                        <div className="row g-3">
                                            {vulnerabilityStatus.map((vuln) => {
                                                const colorRgb = vuln.color === "#ef4444" ? "239, 68, 68" : vuln.color === "#f59e0b" ? "245, 158, 11" : vuln.color === "#f97316" ? "249, 115, 22" : "107, 114, 128";
                                                return (
                                                    <div key={vuln.category} className="col-lg-6 col-md-12">
                                                        <div className="p-4" style={{ backgroundColor: vuln.color + "0f", borderLeft: `4px solid ${vuln.color}`, borderRadius: "8px", background: `linear-gradient(135deg, rgba(${colorRgb}, 0.05) 0%, rgba(0, 0, 0, 0) 100%)`, boxShadow: `0 2px 8px rgba(${colorRgb}, 0.08)` }}>
                                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                                <div>
                                                                    <h6 className="fw-bold text-gray-900 mb-1">{vuln.category} Vulnerabilities</h6>
                                                                    <p className="text-gray-600 fs-6 mb-0">{vuln.action}</p>
                                                                </div>
                                                                <div className="text-center" style={{ background: `rgba(${colorRgb}, 0.1)`, width: "50px", height: "50px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                    <h3 className="fw-bold mb-0 fs-4" style={{ color: vuln.color }}>
                                                                        {vuln.count}
                                                                    </h3>
                                                                </div>
                                                            </div>
                                                            <div className="d-flex gap-2">
                                                                <button type="button" className="btn btn-sm btn-primary fw-bold">
                                                                    View Details
                                                                </button>
                                                                <button type="button" className="btn btn-sm btn-outline-secondary fw-bold">
                                                                    Patch Timeline
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>



                    </div>
                </div>
            </div>
        </>











    );
}
